import {
  Box,
  Radio,
  Typography,
  TextField,
  ListItem,
  Button,
  List,
  Autocomplete,
  Switch,
  SwipeableDrawer,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  MenuItem,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import paymentdashboardservice from "../../../../services/paymentdashboardservice";
import { useForm, Controller } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import Platforms from "../Platforms";
import Category from "../Category";
import SelectCategory from "../SelectCategory";
import campaignservice from "../../../../services/campaignservice";
import CustomizedSnackbars from "../CustomizedSnackbars";
import SelectedPlatforms from "../SelectedPlatforms";
import BrandsDropdown from "../BrandsDropdown";
import paymentportalservice from "../../../../services/paymentportalservice";
import GetCookieByName from "../../../../hooks/GetCookie";
import useAccess from "../../../../hooks/useAccess";
import ShareWithDropdown from "../ShareWithDropdown";
import SalespersonDropdown from "../SalespersonDropdown";

const todayDateValue = () => new Date().toISOString().split("T")[0];

// Mirrors CAMPAIGN_TYPES in influencer_backend_node/models/campaign.js — kept in sync by
// hand since the two apps don't share code, but validated identically on both sides (the
// enum on the Mongoose schema, and Joi in the same file for the raw request body).
const CAMPAIGN_TYPES = [
  "Influencer Marketing",
  "Meme Marketing",
  "Performance Marketing",
  "Talent Management",
  "Content Monetisation",
];

const CampaignSchema = z.object({
  name: z
    .string({
      required_error: "Campaign name is required",
      invalid_type_error: "Campaign name is required",
    })
    .min(3, { message: "Campaign Name must contain at least 3 character(s)" }),
  totalPayment: z
    .string({ required_error: "Total Payment is required" })
    .min(1, { message: "Budget is required" })
    .regex(/^\d+$/, { message: "Only numbers are allowed" }),

  deliverables: z
    .string({ required_error: "Deliverables is required" })
    .min(1, { message: "Deliverables are required" }),

  category: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .min(1, { message: "At least one category must be selected" }),

  campaignType: z.enum(CAMPAIGN_TYPES, {
    errorMap: () => ({ message: "Please select a campaign type" }),
  }),

  platform: z
    .array(z.string())
    .min(1, { message: "Please select at least one platform" }),

  isOfficeExpense: z.boolean(),

  brand: z
    .string({ required_error: "Brand is required" })
    .min(1, { message: "Please select a brand" }),

  // Super-admin-only, and optional even for them: the inputs are never
  // rendered for anyone else, and the API (POST /campaign) rejects revenue
  // from a non-super-admin outright. An empty string therefore means "not
  // recorded yet" — not zero — and is dropped rather than submitted, leaving
  // the campaign's revenue to be filled in later via the Add Revenue dialog.
  // Allows an optional decimal portion — AddRevenueDialog.jsx (the same
  // revenueAmount field, editable after creation) has never restricted this
  // to whole numbers, and a brand's charged amount can legitimately include
  // paise.
  revenueAmount: z
    .string()
    .regex(/^\d*\.?\d*$/, { message: "Enter a valid amount" })
    .optional(),

  revenueLockedDate: z.string().optional(),

  // Ids only — resolved to users by ShareWithDropdown. Applied after the
  // campaign is created (it has no id to share until then), so a failure
  // here can't cost the campaign itself.
  shareWith: z.array(z.string()).optional(),

  // Optional user ids of whoever sold the campaign; sent with the create call.
  salespersons: z.array(z.string()).optional(),
});

function CampaignForm({
  handleOpenCampaignForm,
  handleCloseCampaignForm,
  openCampaignForm,
  handleSnackBarOpen,
  updateLatestCampaign,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // isSubmitting only gates the button's `disabled` prop, which doesn't take effect until
  // React re-renders — not synchronous enough to stop a second click fired in the same tick
  // as the first, before `await trigger()` even resolves. This ref is checked and set
  // synchronously, so it actually closes that window.
  const isSubmittingRef = useRef(false);
  const { isSuperUser } = useAccess();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
    reset,
    trigger,
    setError,
  } = useForm({
    resolver: zodResolver(CampaignSchema),
    defaultValues: {
      isOfficeExpense: false,
      platform: [],
      category: [],
      campaignType: "",
      name: "",
      totalPayment: "",
      deliverables: "",
      brand: "",
      revenueAmount: "",
      // Pre-filled with today because the backend matches the monthly revenue
      // report on this date — a campaign created with revenue but no locked
      // date would silently miss the current month's summary.
      revenueLockedDate: todayDateValue(),
      shareWith: [],
      salespersons: [],
    },
    mode: "onChange",
  });

  const onSubmit = async (formData) => {
    // Checked and set synchronously, before the first await, so a second click landing in
    // the same tick as the first (before React has re-rendered the disabled button) can't
    // slip through and create a duplicate campaign.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Validate all fields before submission
      const isValid = await trigger();
      if (!isValid) {
        console.log("Form has validation errors");
        return;
      }

      const user = JSON.parse(GetCookieByName("user"));

      const submissionData = {
        name: formData.name,
        totalPayment: formData.totalPayment.toString(),
        deliverables: formData.deliverables,
        brand: formData.brand,
        platform: formData.platform,
        category: formData.category,
        campaignType: formData.campaignType,
        isOfficeExpense: formData.isOfficeExpense,
        createdby: user._id,
      };

      if (formData.salespersons?.length) {
        submissionData.salespersons = formData.salespersons;
      }

      if (isSuperUser && formData.revenueAmount) {
        submissionData.revenueAmount = Number(formData.revenueAmount);
        submissionData.revenueLockedDate =
          formData.revenueLockedDate || todayDateValue();
      }

      const response = await paymentportalservice.PostCampaign(submissionData);

      // The backend responds 201 for both success and failure here (e.g. a
      // duplicate name — routes/campaign.js), sending the campaign object
      // back only on an actual create; axios never throws for either case,
      // so a failure has to be detected from the response shape instead —
      // otherwise it reads as a successful create and the revenue/share
      // input just entered is silently discarded with nothing to recover it.
      if (typeof response === "string" || !response?._id) {
        throw new Error(typeof response === "string" ? response : "Failed to create campaign");
      }

      // Sharing needs the campaign's id, so it can only happen once the
      // create call has come back. Reported separately rather than thrown:
      // the campaign does exist at this point, and access can still be fixed
      // from the Share icon on its card, so this must not look like a failed
      // creation.
      const shareWith = isSuperUser ? formData.shareWith || [] : [];
      const newCampaignId = response?._id || response?.id;
      let shareFailed = false;
      if (shareWith.length && newCampaignId) {
        try {
          await paymentportalservice.UpdateCampaignShares(newCampaignId, shareWith);
        } catch (shareError) {
          console.log("Failed to share campaign", shareError);
          shareFailed = true;
        }
      }

      updateLatestCampaign(response);
      resetForm();
      handleCloseCampaignForm();
      // One snackbar, raised last so it isn't overwritten — a share failure
      // has to be the message the user is left with, not the success.
      if (shareFailed) {
        handleSnackBarOpen(
          "error",
          "Campaign created, but sharing failed — set it from the Share icon on the campaign.",
        );
      } else {
        handleSnackBarOpen("success", "Campaign created successfully");
      }
    } catch (error) {
      console.log("Failed to create campaign", error);
      // POST /campaign rejects inactive salespersons with a message starting
      // "Salesperson is not active" — shown on the field itself as well.
      if (String(error.message).startsWith("Salesperson is not active")) {
        setError("salespersons", { type: "server", message: error.message });
      }
      // This endpoint sends its error reason as a plain string body (e.g.
      // "Please use Different Name for Campaign"), not {message}, so
      // error.response?.data is the string to show, not .data?.message —
      // which would always be undefined here and fall through to the
      // generic text below. error.message covers the same-shaped failure
      // thrown just above when the backend reports failure with a 2xx.
      handleSnackBarOpen(
        "error",
        error.response?.data?.message || error.response?.data || error.message || "Failed to create campaign",
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    reset({
      isOfficeExpense: false,
      platform: [],
      category: [],
      campaignType: "",
      name: "",
      totalPayment: "",
      deliverables: "",
      brand: "",
      revenueAmount: "",
      revenueLockedDate: todayDateValue(),
      shareWith: [],
      salespersons: [],
    });
  };

  const handleClose = () => {
    resetForm();
    handleCloseCampaignForm();
  };

  const onError = (errors) => {
    console.log("Validation Errors:", errors);

    // Show first error in snackbar
    const firstError = Object.values(errors)[0];
    if (firstError?.message) {
      handleSnackBarOpen("error", firstError.message);
    }
  };

  return (
    <SwipeableDrawer
      anchor={"right"}
      open={openCampaignForm}
      onClose={handleClose}
      onOpen={handleOpenCampaignForm}
    >
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Box sx={{ paddingX: 3, paddingTop: 3 }} role="presentation">
          <Typography variant="h6" textAlign={"center"} fontWeight={"bold"}>
            Add Campaign
          </Typography>

          <List>
            <ListItem disablePadding>
              <SelectCategory
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                control={control}
              />
            </ListItem>
          </List>

          <List>
            <ListItem disablePadding>
              <TextField
                select
                fullWidth
                label="Type*"
                sx={{ width: 350 }}
                {...register("campaignType")}
                defaultValue=""
                helperText={errors.campaignType?.message}
                error={!!errors.campaignType}
              >
                <MenuItem value="" disabled>
                  Select a campaign type
                </MenuItem>
                {CAMPAIGN_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </ListItem>
          </List>

          <List>
            <ListItem>
              <BrandsDropdown
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                control={control}
              />
            </ListItem>
          </List>

          <List>
            <ListItem disablePadding>
              <TextField
                id="outlined-basic"
                label="Campaign Name*"
                variant="outlined"
                sx={{ width: 350 }}
                {...register("name")}
                helperText={errors.name?.message}
                error={!!errors.name}
              />
            </ListItem>
          </List>

          <List>
            <ListItem disablePadding>
              <TextField
                id="outlined-basic"
                label="Budget*"
                variant="outlined"
                sx={{ width: 350 }}
                {...register("totalPayment")}
                helperText={errors.totalPayment?.message}
                error={!!errors.totalPayment}
                onKeyDown={(e) => {
                  if (
                    ["e", "E", "+", "-", ",", ";", ":", "."].includes(e.key)
                  ) {
                    e.preventDefault();
                  }
                }}
                type="number"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </ListItem>
          </List>

          <List>
            <ListItem disablePadding>
              <Controller
                name="salespersons"
                control={control}
                render={({ field }) => (
                  <SalespersonDropdown
                    value={field.value || []}
                    onChange={field.onChange}
                    disabled={isSubmitting}
                    error={errors.salespersons?.message}
                  />
                )}
              />
            </ListItem>
          </List>

          {/* Revenue is what the brand was charged, as opposed to Budget
              (what gets paid out) — commercially sensitive, so it is shown
              only to a super admin here and stripped from every campaign API
              response for everyone else. */}
          {isSuperUser && (
            <>
              <List>
                <ListItem disablePadding>
                  <TextField
                    label="Revenue Amount (₹)"
                    variant="outlined"
                    sx={{ width: 350 }}
                    {...register("revenueAmount")}
                    helperText={
                      errors.revenueAmount?.message ||
                      "Optional — visible to super admins only"
                    }
                    error={!!errors.revenueAmount}
                    // Unlike Budget above, "." isn't blocked here — a charged
                    // amount can legitimately include paise (see the schema
                    // comment on revenueAmount), and AddRevenueDialog.jsx (the
                    // same field, editable after creation) has always let it
                    // through.
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", ",", ";", ":"].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    type="number"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      min: 0,
                    }}
                  />
                </ListItem>
              </List>

              <List>
                <ListItem disablePadding>
                  <TextField
                    label="Revenue Locked Date"
                    variant="outlined"
                    type="date"
                    sx={{ width: 350 }}
                    {...register("revenueLockedDate")}
                    helperText={
                      errors.revenueLockedDate?.message ||
                      "Month this revenue is reported in"
                    }
                    error={!!errors.revenueLockedDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </ListItem>
              </List>

              <List>
                <ListItem disablePadding>
                  <Controller
                    name="shareWith"
                    control={control}
                    render={({ field }) => (
                      <ShareWithDropdown
                        value={field.value || []}
                        onChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    )}
                  />
                </ListItem>
              </List>
            </>
          )}

          <List>
            <ListItem disablePadding>
              <TextField
                id="outlined-multiline-static"
                label="Deliverables*"
                rows={4}
                multiline
                sx={{ width: 350 }}
                {...register("deliverables")}
                helperText={errors.deliverables?.message}
                error={!!errors.deliverables}
              />
            </ListItem>
          </List>

          <List>
            <ListItem disablePadding>
              <SelectedPlatforms
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                control={control}
              />
            </ListItem>
          </List>

          <List>
            <ListItem>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      name="isOfficeExpense"
                      {...register("isOfficeExpense")}
                    />
                  }
                  label="Office Expense"
                />
              </FormGroup>
            </ListItem>
          </List>

          <Box sx={{ marginTop: 2, textAlign: "center", marginBottom: 3 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitting}
              sx={{ minWidth: 150 }}
            >
              {isSubmitting ? "Adding Campaign..." : "Add Campaign"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ ml: 2, minWidth: 150 }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </SwipeableDrawer>
  );
}

export default CampaignForm;
